import {
  FormResponseResult,
  FormResponseStatus,
  OrderDisplayBaseStatus,
  OrderDisplayStatus,
  OrderStatus,
  OrderTimelineDisplayStatus,
  OrderVisitStatus,
  OrderVisitTimelineDisplayStatus,
  PageValidation,
  ResolveStatusStateProps,
  StatusSidebarStatus,
  StatusSidebarStatusType,
  StatusSidebarStatusTypeInfoNeededMixin,
  TargetConditionReason,
} from './StatusPage.types';
import { paths } from '../../../paths';
import { StatusCardProps } from './StatusCard';
import dayjs from 'dayjs';
import { SelectProps } from '../../atoms';

export function isOrderTimelineStatus(
  value: string
): value is OrderTimelineDisplayStatus {
  return Object.values(OrderTimelineDisplayStatus).includes(
    value as OrderTimelineDisplayStatus
  );
}

export function isOrderVisitTimelineStatus(
  value: string
): value is OrderVisitTimelineDisplayStatus {
  return Object.values(OrderVisitTimelineDisplayStatus).includes(
    value as OrderVisitTimelineDisplayStatus
  );
}

function isOrderVisitStatus(status: string): status is OrderVisitStatus {
  return Object.values(OrderVisitStatus).includes(status as OrderVisitStatus);
}

function getOrderDisplayStatus(status: string) {
  return (
    {
      [OrderStatus.PendingPreTestConsult]:
        OrderDisplayBaseStatus.PendingPreTestConsult,
      [OrderStatus.Cancelled]: OrderDisplayBaseStatus.OptedOut,
      [OrderStatus.PendingApproval]: OrderDisplayBaseStatus.PendingApproval,
      // Everything below will show the order timeline
      [OrderStatus.PWNApproved]: OrderTimelineDisplayStatus.KitPreparing,
      [OrderStatus.NeedsKit]: OrderTimelineDisplayStatus.KitPreparing,
      [OrderStatus.ManualLaboratoryRequest]:
        OrderTimelineDisplayStatus.KitPreparing,
      [OrderStatus.KitMailed]: OrderTimelineDisplayStatus.KitMailed,
      [OrderStatus.SamplesReceived]: OrderTimelineDisplayStatus.SamplesReceived,
      [OrderStatus.SampleQc]: OrderTimelineDisplayStatus.SamplesReceived,
      [OrderStatus.ExomeQc]: OrderTimelineDisplayStatus.SamplesReceived,
      [OrderStatus.SequencingComplete]:
        OrderTimelineDisplayStatus.SequencingComplete,
      [OrderStatus.AnalysisComplete]:
        OrderTimelineDisplayStatus.AnalysisComplete,
      [OrderStatus.PendingConsultScheduling]:
        OrderTimelineDisplayStatus.ReportReady,
      [OrderStatus.ConsultScheduled]: OrderTimelineDisplayStatus.ReportReady,
      [OrderStatus.ReportReady]: OrderTimelineDisplayStatus.ReportReady,
      [OrderStatus.PWNCancelled]: OrderDisplayBaseStatus.Approved,
      [OrderStatus.Paused]: OrderDisplayBaseStatus.Approved,
    }?.[status as OrderStatus] ?? OrderDisplayBaseStatus.Approved
  );
}

export function resolveState({
  status,
  result,
  order,
  submittedTimestamp,
  acceptedInformedConsent,
  pageValidation,
  referenceId,
}: StatusCardProps): OrderDisplayStatus {
  // DO NOT CHANGE THE ORDER OF THESE CHECKS (unless you know what you're doing)

  // If the user's form response has an order assigned, it has higher precedence
  // than the form response statuses.
  if (order?.status) {
    // This is for Patient Visit Statuses
    if (isOrderVisitStatus(order.status)) {
      const visitStatus: OrderVisitStatus = order.status as OrderVisitStatus;
      // Order status will never overlap with genetic tests as backend will ensure that
      if (visitStatus === OrderVisitStatus.Pending) {
        return OrderVisitTimelineDisplayStatus.VisitPending;
      }
      if (visitStatus === OrderVisitStatus.LabResultsPending) {
        return OrderVisitTimelineDisplayStatus.VisitLabResultsPending;
      }
      if (visitStatus == OrderVisitStatus.LabResultsComplete) {
        return OrderVisitTimelineDisplayStatus.VisitLabResultsComplete;
      }
      return OrderDisplayBaseStatus.Approved;
    }
    // All other order statuses go here
    else if (isOrderTimelineStatus(order.status)) {
      return getOrderDisplayStatus(order.status);
    }
  }

  // PreApproved states
  if (
    (status === FormResponseStatus.Approved ||
      status === FormResponseStatus.PreEligible) &&
    !acceptedInformedConsent
  ) {
    if (!pageValidation?.isValid || !referenceId) {
      // Approved but info needed
      return OrderDisplayBaseStatus.PreApprovedInfoNeeded;
    } else {
      // Approved but need to resubmit
      return OrderDisplayBaseStatus.ReadyToSubmit;
    }
  }

  if (!pageValidation?.isValid || !referenceId) {
    return OrderDisplayBaseStatus.InfoNeeded;
  }

  if (!acceptedInformedConsent) {
    return OrderDisplayBaseStatus.ReadyToSubmit;
  }

  // Everything below refers to states after submission
  if (
    result === FormResponseResult.Waitlist &&
    status === FormResponseStatus.Rejected &&
    submittedTimestamp &&
    dayjs().diff(dayjs.unix(submittedTimestamp), 'd') < 14
  ) {
    return OrderDisplayBaseStatus.InReview;
  }
  if (
    (result === FormResponseResult.Waitlist &&
      status === FormResponseStatus.Rejected) ||
    status === FormResponseStatus.PermanentlyRejected ||
    status === FormResponseStatus.Fraudulent
  ) {
    return OrderDisplayBaseStatus.Rejected;
  }
  if (result === FormResponseResult.TrainingResponse) {
    return OrderDisplayBaseStatus.TrainingResponse;
  }
  if (result === FormResponseResult.NotInUnitedStates) {
    return OrderDisplayBaseStatus.NotInUnitedStates;
  }
  if (status === FormResponseStatus.OptedOut) {
    return OrderDisplayBaseStatus.OptedOut;
  }
  if (
    status === FormResponseStatus.InReview ||
    status === FormResponseStatus.Pending ||
    !order
  ) {
    if (
      submittedTimestamp &&
      // AFTER 14 days, we show change to this.
      dayjs().diff(dayjs.unix(submittedTimestamp), 'd') >= 14
    ) {
      return OrderDisplayBaseStatus.Rejected;
    }
    return OrderDisplayBaseStatus.InReview;
  }

  return getOrderDisplayStatus(order.status as string);
}

export function getFirstIncompleteSectionPathFromPageValidation(
  pageValidation?: PageValidation
): string {
  // Don't use this key on the check below

  const firstIncompleteSectionKey: keyof typeof paths.dashboard = (Object.keys(
    pageValidation?.errors ?? {}
  )?.[0] ?? 'status') as keyof typeof paths.dashboard;
  // This is an empty string since typing requires it due to the update
  // relationship to patient route. That route should never called here.
  return paths.dashboard[firstIncompleteSectionKey].getHref('');
}

export function getHumanReadableMissingInformation(
  pageValidation?: PageValidation
): Record<string, string> {
  return Object.fromEntries(
    Object.entries({
      accountFirstName:
        pageValidation?.errors?.['account']?.firstName && 'Your first name',
      accountLastName:
        pageValidation?.errors?.['account']?.lastName && 'Your last name',
      accountPhone:
        pageValidation?.errors?.['account']?.phone && 'Phone number',
      accountEmail:
        pageValidation?.errors?.['account']?.email && 'Email address',
      accountRelationshipToPatient:
        pageValidation?.errors?.['account']?.relationshipToPatient &&
        'Relationship to patient',
      patientFirstName:
        pageValidation?.errors?.['patient']?.firstName &&
        "Patient's first name",
      patientLastName:
        pageValidation?.errors?.['patient']?.lastName && "Patient's last name",
      patientDateOfBirth:
        pageValidation?.errors?.['patient']?.dateOfBirth &&
        "Patient's date of birth",
      patientBiologicalSex:
        pageValidation?.errors?.['patient']?.biologicalSex &&
        "Patient's biological sex",
      patientPhysicians:
        pageValidation?.errors?.['patient']?.physicians &&
        "Patient's primary and treating physicians",
      diagnoses:
        pageValidation?.errors?.['diagnoses']?.diagnoses &&
        'Previous diagnoses',
      geneticTests:
        pageValidation?.errors?.['geneticTests']?.tests &&
        'Familial genetic tests and results',
      labTests:
        pageValidation?.errors?.['labTests']?.tests && 'Lab tests and results',
      assessment:
        pageValidation?.errors?.['assessment']?.conversationEnded &&
        'Symptom assessment',
    }).filter(([, value]) => !!value)
  );
}

export function resolveStatusState(
  props: ResolveStatusStateProps
): StatusSidebarStatus {
  const { isLoading, pageKey, pageValidation } = props;
  const state = resolveState(props);

  if (isLoading) {
    return {
      state: StatusSidebarStatusType.Loading,
    };
  }
  if (pageKey === 'status') {
    const statusState =
      {
        [OrderDisplayBaseStatus.InfoNeeded]:
          StatusSidebarStatusTypeInfoNeededMixin.InfoNeeded,
        [OrderDisplayBaseStatus.PreApprovedInfoNeeded]:
          StatusSidebarStatusTypeInfoNeededMixin.PreApprovedInfoNeeded,
        [OrderDisplayBaseStatus.ReadyToSubmit]:
          StatusSidebarStatusTypeInfoNeededMixin.ReadyToSubmit,
        [OrderDisplayBaseStatus.Rejected]: StatusSidebarStatusType.Rejected,
        [OrderDisplayBaseStatus.TrainingResponse]:
          StatusSidebarStatusType.Rejected,
        [OrderDisplayBaseStatus.NotInUnitedStates]:
          StatusSidebarStatusType.Rejected,
        [OrderDisplayBaseStatus.OptedOut]: StatusSidebarStatusType.OptedOut,
        [OrderDisplayBaseStatus.Waitlist]: StatusSidebarStatusType.Waitlisted,
        [OrderDisplayBaseStatus.InReview]: StatusSidebarStatusType.Pending,
        [OrderDisplayBaseStatus.Pending]: StatusSidebarStatusType.Pending,
        [OrderDisplayBaseStatus.PendingPreTestConsult]:
          StatusSidebarStatusType.PreApproved,
        [OrderDisplayBaseStatus.PendingApproval]:
          StatusSidebarStatusType.Approved,
        [OrderTimelineDisplayStatus.KitPreparing]:
          StatusSidebarStatusType.Approved,
        [OrderTimelineDisplayStatus.KitMailed]:
          StatusSidebarStatusType.Approved,
        [OrderTimelineDisplayStatus.SamplesReceived]:
          StatusSidebarStatusType.Approved,
        [OrderTimelineDisplayStatus.AnalysisComplete]:
          StatusSidebarStatusType.Approved,
        [OrderTimelineDisplayStatus.SequencingComplete]:
          StatusSidebarStatusType.Approved,
        [OrderTimelineDisplayStatus.PendingConsultScheduling]:
          StatusSidebarStatusType.Approved,
        [OrderTimelineDisplayStatus.ConsultScheduled]:
          StatusSidebarStatusType.Approved,
        [OrderTimelineDisplayStatus.ReportReady]:
          StatusSidebarStatusType.Approved,
        [OrderDisplayBaseStatus.Approved]: StatusSidebarStatusType.Approved,
        [OrderVisitTimelineDisplayStatus.VisitPending]:
          StatusSidebarStatusType.Approved,
        [OrderVisitTimelineDisplayStatus.VisitLabResultsPending]:
          StatusSidebarStatusType.Approved,
        [OrderVisitTimelineDisplayStatus.VisitLabResultsComplete]:
          StatusSidebarStatusType.Approved,
      }?.[state] ?? StatusSidebarStatusTypeInfoNeededMixin.InfoNeeded;

    if (
      [
        StatusSidebarStatusTypeInfoNeededMixin.InfoNeeded,
        StatusSidebarStatusTypeInfoNeededMixin.PreApprovedInfoNeeded,
      ].includes(statusState as StatusSidebarStatusTypeInfoNeededMixin)
    ) {
      return {
        progress: 0,
        state: statusState,
      };
    }
    return {
      state: statusState as StatusSidebarStatusType,
    };
  }
  if (pageValidation?.completeness?.[pageKey] === 1) {
    return {
      state: StatusSidebarStatusType.Complete,
    };
  }
  return {
    state: StatusSidebarStatusTypeInfoNeededMixin.InfoNeeded,
    progress: pageValidation?.completeness?.[pageKey] ?? 0,
  };
}

export enum OrderStatusGroupEnum {
  IntakeSubmitted = 'Intake Submitted',
  TestingInProgress = 'Testing In-progress',
  TestingComplete = 'Testing Complete',
}

export const GROUPED_ORDER_STATUS_ITEMS: SelectProps['items'] = [
  {
    key: OrderStatusGroupEnum.IntakeSubmitted,
    label: OrderStatusGroupEnum.IntakeSubmitted,
    value: OrderStatusGroupEnum.IntakeSubmitted,
  },
  {
    key: OrderStatus.PendingPreTestConsult,
    label: 'Pending pre-test consult',
    value: OrderStatus.PendingPreTestConsult,
  },
  {
    key: OrderStatusGroupEnum.TestingInProgress,
    label: OrderStatusGroupEnum.TestingInProgress,
    value: OrderStatusGroupEnum.TestingInProgress,
  },
  {
    key: OrderStatusGroupEnum.TestingComplete,
    label: OrderStatusGroupEnum.TestingComplete,
    value: OrderStatusGroupEnum.TestingComplete,
  },
];

// The `value` in these objects will replace the chosen dropdown option in
// the filter component when the Apply button is pressed.
export const ORDER_STATUS_GROUP_TO_STATUSES_ITEMS = [
  {
    key: OrderStatusGroupEnum.IntakeSubmitted,
    label: 'Intake Submitted',
    value: [],
  },
  {
    key: OrderStatus.PendingPreTestConsult,
    label: 'Pending pre-test consult',
    value: [OrderStatus.PendingPreTestConsult],
  },
  {
    key: OrderStatusGroupEnum.TestingInProgress,
    label: 'Testing In-progress',
    value: [
      OrderStatus.PendingApproval,
      OrderStatus.PWNApproved,
      OrderStatus.NeedsKit,
      OrderStatus.ManualLaboratoryRequest,
      OrderStatus.KitMailed,
      OrderStatus.SamplesReceived,
      OrderStatus.SampleQc,
      OrderStatus.ExomeQc,
      OrderStatus.SequencingComplete,
      OrderStatus.Cancelled,
      OrderStatus.Paused,
      OrderStatus.PWNCancelled,
    ],
  },
  {
    key: OrderStatusGroupEnum.TestingComplete,
    label: 'Testing Complete',
    value: [
      OrderStatus.AnalysisComplete,
      OrderStatus.PendingConsultScheduling,
      OrderStatus.ConsultScheduled,
      OrderStatus.ReportReady,
    ],
  },
];

export const ORDER_STATUS_ITEMS: SelectProps['items'] = [
  {
    key: OrderStatus.PendingPreTestConsult,
    label: 'Pending pre-test consult',
    value: OrderStatus.PendingPreTestConsult,
  },
  {
    key: OrderStatus.PendingApproval,
    label: 'Testing In-progress',
    value: OrderStatus.PendingApproval,
  },
  {
    key: OrderStatus.PWNApproved,
    label: 'Testing In-progress',
    value: OrderStatus.PWNApproved,
  },
  {
    key: OrderStatus.NeedsKit,
    label: 'Testing In-progress',
    value: OrderStatus.NeedsKit,
  },
  {
    key: OrderStatus.ManualLaboratoryRequest,
    label: 'Testing In-progress',
    value: OrderStatus.ManualLaboratoryRequest,
  },
  {
    key: OrderStatus.KitMailed,
    label: 'Testing In-progress',
    value: OrderStatus.KitMailed,
  },
  {
    key: OrderStatus.SamplesReceived,
    label: 'Testing In-progress',
    value: OrderStatus.SamplesReceived,
  },
  {
    key: OrderStatus.SampleQc,
    label: 'Testing In-progress',
    value: OrderStatus.SampleQc,
  },
  {
    key: OrderStatus.ExomeQc,
    label: 'Testing In-progress',
    value: OrderStatus.ExomeQc,
  },
  {
    key: OrderStatus.SequencingComplete,
    label: 'Testing In-progress',
    value: OrderStatus.SequencingComplete,
  },
  {
    key: OrderStatus.AnalysisComplete,
    label: 'Testing Complete',
    value: OrderStatus.AnalysisComplete,
  },
  {
    key: OrderStatus.PendingConsultScheduling,
    label: 'Testing Complete',
    value: OrderStatus.PendingConsultScheduling,
  },
  {
    key: OrderStatus.ConsultScheduled,
    label: 'Testing Complete',
    value: OrderStatus.ConsultScheduled,
  },
  {
    key: OrderStatus.ReportReady,
    label: 'Testing Complete',
    value: OrderStatus.ReportReady,
  },
  {
    key: OrderStatus.Cancelled,
    label: 'Testing In-progress',
    value: OrderStatus.Cancelled,
  },
  {
    key: OrderStatus.PWNCancelled,
    label: 'Testing In-progress',
    value: OrderStatus.PWNCancelled,
  },
  {
    key: OrderStatus.Paused,
    label: 'Testing In-progress',
    value: OrderStatus.Paused,
  },
];

// Reasons that require verification to be considered a target condition.
// These should match `FormResponseTargetConditionRelationship.is_confirmed_self_reported()`
export const TARGET_CONDITIONS_NEEDING_VERIFICATION = [
  TargetConditionReason.ManualPOI,
  TargetConditionReason.SelfReportedGene,
  TargetConditionReason.SelfReportedDiagnosis,
  TargetConditionReason.FamilyReportedGene,
  TargetConditionReason.FamilyReportedDiagnosis,
];

// These reasons should be in the order of precedence in terms of what's the
// most important for the biotech users. The ones at the top will show first
// whenever we have multiple reasons (i.e. target patients page, reasons column).
export const TARGET_CONDITION_REASON_ITEMS: SelectProps['items'] = [
  {
    key: TargetConditionReason.PGSequencedPositive,
    label: 'Positive Result',
    value: TargetConditionReason.PGSequencedPositive,
  },
  {
    key: TargetConditionReason.PGSequencedPotentiallyPositive,
    label: 'Potentially Positive Result',
    value: TargetConditionReason.PGSequencedPotentiallyPositive,
  },
  {
    key: TargetConditionReason.PGSequencedUncertain,
    label: 'Uncertain Result',
    value: TargetConditionReason.PGSequencedUncertain,
  },
  {
    key: TargetConditionReason.PGSequencedCarrier,
    label: 'Carrier Result',
    value: TargetConditionReason.PGSequencedUncertain,
  },
  {
    key: TargetConditionReason.ManualPOI,
    label: 'Other Patient of Interest',
    value: TargetConditionReason.ManualPOI,
  },
  {
    key: TargetConditionReason.SelfReportedGene,
    label: 'Self-Reported Genetic Result',
    value: TargetConditionReason.SelfReportedGene,
  },
  {
    key: TargetConditionReason.SelfReportedDiagnosis,
    label: 'Self-Reported Diagnosis',
    value: TargetConditionReason.SelfReportedDiagnosis,
  },
  {
    key: TargetConditionReason.FamilyReportedGene,
    label: 'Self-Reported Family History',
    value: TargetConditionReason.FamilyReportedGene,
  },
  {
    key: TargetConditionReason.FamilyReportedDiagnosis,
    label: 'Self-Reported Family History',
    value: TargetConditionReason.FamilyReportedDiagnosis,
  },
  {
    key: TargetConditionReason.Offered,
    label: 'Testing Offered',
    value: TargetConditionReason.Offered,
  },
];
